import { useTranslation } from 'react-i18next';
import { Database, Shield, RefreshCw, FileCheck, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { SEO, getBreadcrumbSchema } from '@/hooks/useSEO';
import { useLanguage } from '@/hooks/useLanguage';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function DataInServicesPage() {
  const { t } = useTranslation();
  const { lang } = useLanguage();

  const sources = [
    'information.dataInServices.sources.postalOperators',
    'information.dataInServices.sources.authorities',
    'information.dataInServices.sources.registers',
  ];

  const deliverables = [
    'information.dataInServices.deliverables.addresses',
    'information.dataInServices.deliverables.postalCodes',
    'information.dataInServices.deliverables.geography',
    'information.dataInServices.deliverables.geodata',
  ];

  return (
    <div className="container py-12 lg:py-16">
      <SEO
        title={t('information.dataInServices.title')}
        description={t('meta.dataInServices')}
        path="/information/data-i-vara-tjanster"
        jsonLd={getBreadcrumbSchema([
          { name: t('common.home'), url: `https://postnummerservice.se/${lang}` },
          { name: t('common.information'), url: `https://postnummerservice.se/${lang}/information/data-i-vara-tjanster` },
          { name: t('information.dataInServices.title'), url: `https://postnummerservice.se/${lang}/information/data-i-vara-tjanster` },
        ])}
      />
      <div className="max-w-3xl mx-auto">
        {/* Hero */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <Database className="w-6 h-6 text-primary" />
            <h1 className="text-3xl md:text-4xl font-bold text-foreground">
              {t('information.dataInServices.title')}
            </h1>
          </div>
          <p className="text-lg text-muted-foreground leading-relaxed">
            {t('information.dataInServices.subtitle')}
          </p>
        </div>

        {/* Main content as accordion */}
        <Accordion type="multiple" defaultValue={['sources', 'quality', 'maintenance', 'deliverables']} className="space-y-2">
          {/* Sources */}
          <AccordionItem value="sources" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline gap-3">
              <span className="flex items-center gap-3 text-left">
                <Shield className="w-5 h-5 text-secondary flex-shrink-0" />
                <span className="font-semibold">{t('information.dataInServices.sourcesTitle')}</span>
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <p className="text-muted-foreground mb-4">{t('information.dataInServices.sourcesDescription')}</p>
              <div className="flex flex-wrap gap-2">
                {sources.map((source, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 bg-accent text-accent-foreground text-sm font-medium px-3 py-1.5 rounded-full">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {t(source)}
                  </span>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Quality */}
          <AccordionItem value="quality" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline gap-3">
              <span className="flex items-center gap-3 text-left">
                <FileCheck className="w-5 h-5 text-secondary flex-shrink-0" />
                <span className="font-semibold">{t('information.dataInServices.qualityTitle')}</span>
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <p className="text-muted-foreground leading-relaxed">{t('information.dataInServices.qualityDescription')}</p>
            </AccordionContent>
          </AccordionItem>

          {/* Maintenance */}
          <AccordionItem value="maintenance" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline gap-3">
              <span className="flex items-center gap-3 text-left">
                <RefreshCw className="w-5 h-5 text-secondary flex-shrink-0" />
                <span className="font-semibold">{t('information.dataInServices.maintenanceTitle')}</span>
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <p className="text-muted-foreground leading-relaxed mb-4">{t('information.dataInServices.maintenanceDescription')}</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-muted/60 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-primary">10-15%</p>
                  <p className="text-xs text-muted-foreground mt-1">{t('information.dataInServices.stats.errorRate')}</p>
                </div>
                <div className="bg-muted/60 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-primary">250 000+</p>
                  <p className="text-xs text-muted-foreground mt-1">{t('information.dataInServices.stats.changesPerYear')}</p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Deliverables */}
          <AccordionItem value="deliverables" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline gap-3">
              <span className="flex items-center gap-3 text-left">
                <Database className="w-5 h-5 text-secondary flex-shrink-0" />
                <span className="font-semibold">{t('information.dataInServices.deliverablesTitle')}</span>
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid gap-2 sm:grid-cols-2">
                {deliverables.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>{t(item)}</span>
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
}
